import type { Client } from '../client';
import type { Doctor, DoctorRequest, Department, DepartmentRequest } from '../types';

export function hospitalEndpoints(client: Client) {
  return {
    // ── Doctors ────────────────────────────────────────────────────
    // With departmentId: only that department's doctors plus unassigned
    // ones, department members first — the approval picker's order.
    listDoctors: (hospitalId: string, departmentId?: number) =>
      client.get<Doctor[]>(
        `/api/hospitals/${hospitalId}/doctors${
          departmentId != null ? `?department_id=${departmentId}` : ''
        }`,
      ),

    createDoctor: (hospitalId: string, data: DoctorRequest) =>
      client.post<Doctor>(`/api/hospitals/${hospitalId}/doctors`, data),

    updateDoctor: (hospitalId: string, doctorId: number, data: DoctorRequest) =>
      client.put<Doctor>(
        `/api/hospitals/${hospitalId}/doctors/${doctorId}`,
        data,
      ),

    deleteDoctor: (hospitalId: string, doctorId: number) =>
      client.del<{ ok: boolean }>(
        `/api/hospitals/${hospitalId}/doctors/${doctorId}`,
      ),

    // ── Departments ────────────────────────────────────────────────
    listDepartments: (hospitalId: string) =>
      client.get<Department[]>(`/api/hospitals/${hospitalId}/departments`),

    createDepartment: (hospitalId: string, data: DepartmentRequest) =>
      client.post<Department>(
        `/api/hospitals/${hospitalId}/departments`,
        data,
      ),

    updateDepartment: (
      hospitalId: string,
      depId: number,
      data: DepartmentRequest,
    ) =>
      client.put<Department>(
        `/api/hospitals/${hospitalId}/departments/${depId}`,
        data,
      ),

    deleteDepartment: (hospitalId: string, depId: number) =>
      client.del<{ ok: boolean }>(
        `/api/hospitals/${hospitalId}/departments/${depId}`,
      ),

    // ── Routing (WS1) ──────────────────────────────────────────────
    listRouting: (hospitalId: string) =>
      client.get<RoutingRule[]>(`/api/hospitals/${hospitalId}/routing`),

    upsertRouting: (hospitalId: string, data: {
      match_type?: string;
      match_value: string;
      department_id: number;
      /** GlobeMed idSpecialty — matching prefers it over match_value. */
      specialty_code?: string | null;
    }) =>
      client.post<RoutingRule>(`/api/hospitals/${hospitalId}/routing`, data),

    deleteRouting: (hospitalId: string, routingId: number) =>
      client.del<void>(`/api/hospitals/${hospitalId}/routing/${routingId}`),

    /** GlobeMed's own specialty catalogue — the exact strings that land on
     * cases. Not hospital-scoped; it is the same list for every tenant. */
    listSpecialties: () => client.get<GlobeMedSpecialty[]>('/api/specialties'),

    // ── Hospital team (rep self-service) ───────────────────────────
    listUsers: (hospitalId: string) =>
      client.get<HospitalUser[]>(`/api/hospitals/${hospitalId}/users`),

    createUser: (hospitalId: string, data: HospitalUserCreate) =>
      client.post<HospitalUser>(`/api/hospitals/${hospitalId}/users`, data),

    deleteUser: (hospitalId: string, userId: number) =>
      client.del<{ ok: boolean }>(`/api/hospitals/${hospitalId}/users/${userId}`),
  };
}

export interface GlobeMedSpecialty {
  /** GlobeMed's stable idSpecialty, e.g. "370". */
  code: string;
  description: string;
}

export interface RoutingRule {
  id: number;
  match_type: string;
  match_value: string;
  department_id: number;
  /** Null on rules made before the catalogue existed, or typed by hand. */
  specialty_code?: string | null;
  department_name?: string;
}

// ── Hospital team (rep self-service user management) ─────────────────
export type HospitalRole =
  | 'hospital_rep'
  | 'coordinator'
  | 'department_head'
  | 'doctor'
  | 'intake_coordinator';

export interface HospitalUser {
  id: number;
  username: string;
  display_name: string;
  role: string;
  hospital_id: string | null;
  department_id: number | null;
  doctor_id: number | null;
  created_at: string;
}

export interface HospitalUserCreate {
  username: string;
  password: string;
  display_name: string;
  role: HospitalRole;
  department_id?: number | null;
  doctor_id?: number | null;
}
